import { DeliverySheetSizeHelper } from "./DeliverySheetSizeHelper.mjs";
import { TransformHelper } from "./TransformHelper.mjs";
import { convertCMToMM, convertInchesToMM, formatMM } from "./UnitConverter.mjs";
import { SizeConverter } from "./SizeConverter.mjs";
export class SizeHelper {
    constructor(attrComputer, productItem) {
        this.attrComputer = attrComputer;
        this.productItem = productItem;
        this.fixedSize = false;
        this.transform = new TransformHelper(this);
        this.deliverySheet = new DeliverySheetSizeHelper(productItem);
        this.minSize = new SizeConverter(productItem);
        this.maxSize = new SizeConverter(productItem);
        this.maxSizeOtherSide = new SizeConverter(productItem);
    }
    toString() {
        return `|${this.width.mm}|${this.height.mm}|${this.minSize.mm}|${this.maxSize.mm}|${this.fixedSize}|${this.isImperialUnits()}|`;
    }
    /**
     * Evaluates maxSize, minSize, maxSizeOtherSide, fixedSize.
     */
    evaluate() {
        this.evaluateMaxSize();
        this.evaluateMinSize();
        this.evaluateMaxSizeOtherSide();
        this.evaluateFixedSize();
    }
    getSizeOptions() {
        return this.attrComputer.getSuggestedValues('size');
    }
    canBeProductionLine(productionLine) {
        return !this.attrComputer.isConstrained('production_line', productionLine);
    }
    /**
     * Creates a displayable measurement string.
     *
     * @param measureInInchesOrCM The localized measurement value in inches or centimeters.
     * @returns A formatted and displayable string, such as '20 cm' or '4 "'.
     */
    formatDisplayableMeassure(measureInInchesOrCM) {
        return `${measureInInchesOrCM} ${this.meassureDisplayAbbr}`;
    }
    /**
     * Takes a size string and extracts the numbers, but does NOT convert it to millimeters.
     *
     * @param sizeString The size string, such as 2" x 2" or 10 x 10 cm.
     * @returns An object with the width and height extracted.
     */
    extractMeassuresFromString(sizeString) {
        var _a, _b;
        const [width, height] = ((_b = (_a = sizeString.match(/([\d]{1,3}\.?[\d]{1,3})|([\d]{1,3})/g)) === null || _a === void 0 ? void 0 : _a.map(Number)) !== null && _b !== void 0 ? _b : []).slice(-2);
        return { width, height };
    }
    /**
     * Converts the localized measurement value to millimeters and sets the value.
     *
     * @param attributeName The name of the attribute.
     * @param measureInInchesOrCM The measurement value in inches or centimeters.
     * @returns The measurement value set in millimeters.
     */
    setMeasureAttrLocalized(attributeName, measureInInchesOrCM) {
        return this.setMeasureAttrInMM(attributeName, this.localizedToMM(measureInInchesOrCM));
    }
    /**
     * Converts the localized measurement value to millimeters.
     *
     * @param measureInInchesOrCM The measurement value in inches or centimeters.
     * @returns The measurement value in millimeters.
     */
    localizedToMM(measureInInchesOrCM) {
        return this.isImperialUnits() ? convertInchesToMM(measureInInchesOrCM) : convertCMToMM(measureInInchesOrCM);
    }
    /**
     * Sets the measurement value in millimeters.
     *
     * @param attributeName The name of the attribute.
     * @param measureInMM The measurement value in millimeters.
     * @returns The measurement value set in millimeters.
     */
    setMeasureAttrInMM(attributeName, measureInMM) {
        attributeName = this.sanitizeMeasureAttrName(attributeName);
        if (this.maxSize.mm && measureInMM > this.maxSize.mm) {
            measureInMM = this.maxSize.mm;
        }
        else if (this.minSize.mm && measureInMM < this.minSize.mm) {
            measureInMM = this.minSize.mm;
        }
        measureInMM = formatMM(measureInMM);
        this.productItem.setAttribute(attributeName, measureInMM);
        return measureInMM;
    }
    /**
     * Creates a SizeConverter instance.
     *
     * @param mm The value in millimeters.
     * @returns A SizeConverter instance to access the value in different units.
     */
    getSizeByMM(mm) {
        return new SizeConverter(this.productItem, mm);
    }
    /**
     * Creates a SizeConverter instance.
     *
     * @param attributeName The attribute name.
     * @returns A SizeConverter instance to access the value in different units.
     */
    getSizeByAttribute(attributeName) {
        attributeName = this.sanitizeMeasureAttrName(attributeName);
        const mm = Number(this.productItem.getAttribute(attributeName) || '0');
        return this.getSizeByMM(mm);
    }
    /**
     * Determines whether the product item uses imperial units.
     */
    isImperialUnits() {
        var _a;
        return ((_a = this.productItem.getAttribute('imperial_units')) === null || _a === void 0 ? void 0 : _a.toString()) === 'true';
    }
    /**
     * Determines whether the product item uses fixed size.
     */
    isSizeFixed() {
        return this.fixedSize;
    }
    /**
     * Determines whether the product item has maxSizeOtherSide.
     */
    hasMaxSizeOtherSide() {
        return this.maxSizeOtherSide.mm != this.maxSize.mm;
    }
    get sqm() {
        const quantity = Number(this.productItem.getAttribute('quantity'));
        return (this.width.mm * this.height.mm * quantity) / 1000000;
    }
    /**
     * Returns either '"' or 'cm' depending on whether the product item uses imperial units.
     */
    get meassureDisplayAbbr() {
        return this.isImperialUnits() ? '"' : 'cm';
    }
    /**
     * Creates a SizeConverter instance to access the width in different units.
     */
    get width() {
        return this.getSizeByAttribute('width_mm');
    }
    /**
     * Creates a SizeConverter instance to access the height in different units.
     */
    get height() {
        return this.getSizeByAttribute('height_mm');
    }
    evaluateFixedSize() {
        let fixedSize = this.productItem.getAttribute('fixed_size');
        if (fixedSize === undefined) {
            fixedSize = this.attrComputer.isInFilteredValues('fixed_size', true);
        }
        this.fixedSize = fixedSize;
    }
    evaluateMaxSize() {
        var _a;
        let maxSizeBasedOnProductionLine = this.canBeProductionLine('digital') ? SizeHelper.MAX_SIZE_MM_DIGITAL : SizeHelper.MAX_SIZE_MM_LASER;
        let maxSizeMM = this.productItem.getAttribute('max_size_mm');
        if (!maxSizeMM) {
            maxSizeMM = (_a = this.attrComputer.getHighestAvailableValue('max_size_mm')) !== null && _a !== void 0 ? _a : undefined;
        }
        if (this.productItem.getProductName() == 'labels_on_roll' || this.productItem.getAttribute('material') == 'gitd_industrial') {
            const values = this.attrComputer.getFilteredValues('max_size_mm');
            if (values.length && values[0] < SizeHelper.MAX_SIZE_MM_DIGITAL) {
                maxSizeMM = values[0];
                maxSizeBasedOnProductionLine = SizeHelper.MAX_SIZE_MM_DIGITAL;
            }
        }
        else if (!this.attrComputer.useFilters && !this.canBeProductionLine('digital')) {
            maxSizeBasedOnProductionLine = 800;
        }
        this.maxSize.mm = Math.min(Number(maxSizeMM) || Infinity, maxSizeBasedOnProductionLine);
    }
    evaluateMinSize() {
        var _a;
        let minSizeMM = this.productItem.getAttribute('min_size_mm');
        if (!minSizeMM) {
            minSizeMM = (_a = this.attrComputer.getLowestAvailableValue('min_size_mm')) !== null && _a !== void 0 ? _a : SizeHelper.MIN_SIZE_MM_DEFAULT;
        }
        this.minSize.mm = minSizeMM;
    }
    evaluateMaxSizeOtherSide() {
        let maxSizeOtherSide = this.productItem.getAttribute('max_size_other_side_mm');
        if (!maxSizeOtherSide) {
            const values = this.attrComputer.getFilteredValues('max_size_other_side_mm');
            if (values.length) {
                maxSizeOtherSide = values[0];
            }
            else if (!this.attrComputer.useFilters && !this.canBeProductionLine('digital')) {
                maxSizeOtherSide = SizeHelper.MAX_SIZE_MM_LASER;
            }
        }
        if (!this.maxSize.mm) {
            this.evaluateMaxSize();
        }
        if (!this.minSize.mm) {
            this.evaluateMinSize();
        }
        if (!maxSizeOtherSide || maxSizeOtherSide > this.maxSize.mm) {
            maxSizeOtherSide = this.maxSize.mm;
        }
        else if (maxSizeOtherSide < this.minSize.mm) {
            maxSizeOtherSide = this.minSize.mm;
        }
        this.maxSizeOtherSide.mm = maxSizeOtherSide;
    }
    sanitizeMeasureAttrName(attributeName) {
        if (attributeName.indexOf('_mm') < 0) {
            attributeName += '_mm';
        }
        return attributeName;
    }
}
SizeHelper.MAX_SIZE_MM_DIGITAL = 1200;
SizeHelper.MAX_SIZE_MM_LASER = 275;
SizeHelper.MIN_SIZE_MM_DEFAULT = 25;
